import React, { useState, useEffect, useCallback } from 'react';

const LunchForm = ({ user, onSubmit, lunch = null }) => {
  // Controlled component state
  const [formData, setFormData] = useState({
    custom_title: '',
    day: '',
    time_from: '',
    time_to: '',
    notes: ''
  });

  // Update form data when lunch prop changes (for edit mode)
  useEffect(() => {
    if (lunch) {
      console.log('🔍 [LUNCH FORM] Updating form with lunch data:', lunch);
      
      // Convert date format if needed (dd/mm/yyyy to yyyy-mm-dd for input[type="date"])
      let formattedDay = lunch.day || '';
      if (formattedDay && formattedDay.includes('/')) {
        const [day, month, year] = formattedDay.split('/');
        formattedDay = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
      
      setFormData({
        custom_title: lunch.custom_title || lunch.property_title || '',
        day: formattedDay,
        time_from: lunch.time_from || '',
        time_to: lunch.time_to || '',
        notes: lunch.notes || ''
      });
    } else {
      // Reset form for new lunch
      setFormData({
        custom_title: '',
        day: '',
        time_from: '',
        time_to: '',
        notes: ''
      });
    }
  }, [lunch]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []); // No dependencies to prevent re-creation
  
  // Set up window function ONLY ONCE - NO DEPENDENCIES
  useEffect(() => {
    console.log('🔍 [LUNCH FORM] Setting up window.lunchFormSubmit function - TRULY ONE TIME ONLY');
    
    window.lunchFormSubmit = () => {
      // Get current form values directly from DOM (like original working version)
      const form = document.getElementById('lunch-form');
      if (!form) {
        console.error('🔍 [LUNCH FORM] Form not found');
        return false;
      }

      const formDataDOM = new FormData(form);
      const data = {
        user_id: user?.id || '',
        category_id: 1,
        custom_title: formDataDOM.get('custom_title') || 'Lunch',
        day: formDataDOM.get('day') || '',
        time_from: formDataDOM.get('time_from') || '',
        time_to: formDataDOM.get('time_to') || '',
        notes: formDataDOM.get('notes') || 'Lunch Event'
      };

      // Convert date format if needed (yyyy-mm-dd to dd/mm/yyyy for backend)
      if (data.day && data.day.includes('-')) {
        const [year, month, day] = data.day.split('-');
        data.day = `${day}/${month}/${year}`;
      }

      console.log('🔍 [LUNCH FORM] Final data to submit:', data);
      
      // Enhanced validation with better error messages
      if (!data.user_id) {
        console.error('🔍 [LUNCH FORM] User ID is missing:', data.user_id);
        alert('User ID is missing. Please try refreshing the page.');
        return false;
      }
      if (!data.day) {
        console.error('🔍 [LUNCH FORM] Day is missing:', data.day);
        alert('Day is required');
        return false;
      }
      if (!data.time_from) {
        console.error('🔍 [LUNCH FORM] Time from is missing:', data.time_from);
        alert('Time from is required');
        return false;
      }
      if (!data.time_to) {
        console.error('🔍 [LUNCH FORM] Time to is missing:', data.time_to);
        alert('Time to is required');
        return false;
      }

      // Validate time format
      if (!/^\d{2}:\d{2}$/.test(data.time_from)) {
        console.error('🔍 [LUNCH FORM] Invalid time_from format:', data.time_from);
        alert('Time from must be in HH:MM format');
        return false;
      }
      if (!/^\d{2}:\d{2}$/.test(data.time_to)) {
        console.error('🔍 [LUNCH FORM] Invalid time_to format:', data.time_to);
        alert('Time to must be in HH:MM format');
        return false;
      }

      console.log('✅ [LUNCH FORM] All validation passed, calling onSubmit');
      onSubmit(data);
      return true;
    };
    
    return () => {
      // Cleanup function
      if (window.lunchFormSubmit) {
        delete window.lunchFormSubmit;
      }
    };
  }, []); // NO DEPENDENCIES - TRULY ONE TIME ONLY

  const FormGroup = ({ label, children, required = false }) => (
    <div style={{display: 'table', width: '100%', marginBottom: '8px'}}>
      <label style={{display: 'table-cell', width: '150px', textAlign: 'right', paddingRight: '10px', verticalAlign: 'middle', fontSize: '12px'}}>
        {label} {required && <span style={{color: 'red'}}>*</span>}
      </label>
      <div style={{display: 'table-cell', verticalAlign: 'middle'}}>
        {children}
      </div>
    </div>
  );

  const inputStyle = {
    width: '300px',
    height: '30px',
    fontSize: '12px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    padding: '6px 12px',
    outline: 'none',
    boxSizing: 'border-box',
    backgroundColor: 'white'
  };

  return (
    <form id="lunch-form" style={{padding: '20px'}}>
      <input name="user_id" type="hidden" defaultValue={user?.id || ''} />
      <input name="category_id" type="hidden" defaultValue="1" />

      <FormGroup label="Title">
        <input
          name="custom_title"
          type="text"
          placeholder="Lunch title"
          style={inputStyle}
          defaultValue={formData.custom_title}
          key={`title-${formData.custom_title}`}
        />
      </FormGroup>

      <FormGroup label="Day" required>
        <input
          name="day"
          type="date"
          style={inputStyle}
          defaultValue={formData.day}
          key={`day-${formData.day}`}
        />
      </FormGroup>

      <FormGroup label="Time From" required>
        <input
          name="time_from"
          type="time"
          style={inputStyle}
          defaultValue={formData.time_from}
          key={`time_from-${formData.time_from}`}
        />
      </FormGroup>

      <FormGroup label="Time To" required>
        <input
          name="time_to"
          type="time"
          style={inputStyle}
          defaultValue={formData.time_to}
          key={`time_to-${formData.time_to}`}
        />
      </FormGroup>

      <FormGroup label="Notes">
        <textarea
          name="notes"
          rows="4"
          style={{
            ...inputStyle,
            height: '80px',
            resize: 'none'
          }}
          defaultValue={formData.notes}
          key={`notes-${formData.notes}`}
        />
      </FormGroup>
    </form>
  );
};

export default LunchForm; 